import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTable } from 'react-table';
import Spinner from "react-bootstrap/Spinner";
import Container from "react-bootstrap/Container";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import BTable from 'react-bootstrap/Table';
import { Link } from "wouter";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf } from '@fortawesome/free-solid-svg-icons';

import QuizApi from '../QuizApi';
import AdminHeader from './AdminHeader';
import { absURL } from '../utils';

function Table({ columns, data }) {
  const { getTableProps, headerGroups, rows, prepareRow } = useTable({ columns, data });
  return (
    <div className="adminTableScroll">
      <BTable striped hover size="md" {...getTableProps()}>
        <thead>
          {headerGroups.map(headerGroup => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map(column => (
                <th {...column.getHeaderProps()}>{column.render('Header')}</th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {rows.map(row => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map(cell => (
                  <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </BTable>
    </div>
  );
}

export default function AdminQuizInstanceList(props) {

  const { quizId } = props;

  const [shouldFetchList, setShouldFetchList] = useState(true);
  const [loading, setLoading] = useState(true);
  const [quizName, setQuizName] = useState("");
  const [quizInstanceList, setQuizInstanceList] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [downloadingReportId, setDownloadingReportId] = useState(null);

  // ==================================================================
  // Downloads the PDF report via fetch + Blob rather than window.open.
  // window.open triggers a full top-level navigation, which CRA's dev
  // server proxy does NOT forward to the backend (it bypasses the proxy
  // for requests that look like page navigations, i.e. Accept: text/html,
  // to avoid breaking client-side routing) - so in local dev it silently
  // falls back to the SPA shell instead of hitting the API. A fetch()
  // request doesn't have that Accept header, so it proxies correctly in
  // both dev and production, and this also avoids leaving a stray blank
  // tab open.
  // ==================================================================
  const downloadReport = useCallback(async (accessId) => {
    setDownloadingReportId(accessId);
    try {
      const resp = await fetch(absURL(`/api/quiz/${accessId}/report`));
      const contentType = resp.headers.get('content-type') || '';

      if (!resp.ok || !contentType.includes('application/pdf')) {
        alert('Kunde inte skapa PDF-rapporten.');
        return;
      }

      const blob = await resp.blob();
      const disposition = resp.headers.get('content-disposition') || '';
      const filenameMatch = disposition.match(/filename="([^"]+)"/);
      const filename = filenameMatch ? filenameMatch[1] : 'quiz-rapport.pdf';

      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (e) {
      alert('Kunde inte skapa PDF-rapporten.');
    } finally {
      setDownloadingReportId(null);
    }
  }, []);

  useEffect(() => {
    if (shouldFetchList) {
      const fetch = async () => {
        setLoading(true);
        const result = await QuizApi.userQuizInstanceListGet(quizId);
        setQuizInstanceList(result?.response?.quizInstanceList ?? []);
        if (result?.response?.quiz?.name) {
          setQuizName(result.response.quiz.name);
        }
        setLoading(false);
        setShouldFetchList(false);
      };
      fetch();
    }
  }, [shouldFetchList, quizId]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    await QuizApi.quizInstanceDelete(deleteTarget.id);
    setDeleting(false);
    setDeleteTarget(null);
    setShouldFetchList(true);
  }, [deleteTarget]);

  const columns = useMemo(
    () => [
      {
        Header: 'Lista över omgångar: ' + quizName,
        columns: [
          {
            Header: 'Startades',
            accessor: row => {
              if (!row.executedAt) return "Ej startad";
              const d = new Date(row.executedAt);
              return d.toLocaleDateString() + " " + d.toLocaleTimeString();
            }
          },
          {
            Header: 'Avslutades',
            accessor: row => {
              if (row.manuallyEndedAt) return new Date(row.manuallyEndedAt).toLocaleString();
              if (!row.endsAt) return "Ej startad";
              const endsAt = new Date(row.endsAt);
              return endsAt > new Date() ? "Aktiv" : endsAt.toLocaleString();
            }
          },
          {
            accessor: "id",
            Cell: row => (
              <div>
                <Button
                  onClick={() => window.open(absURL(`/${row.row.original.defaultAccessId}/result`), "_blank")}
                  className="btn-quizPrimaryDark btn-sm mr-1">
                  {!row.row.original.executedAt ? "Lobby" : "Resultat"}
                </Button>
                {!!row.row.original.executedAt &&
                  <Button
                    onClick={() => downloadReport(row.row.original.defaultAccessId)}
                    disabled={downloadingReportId === row.row.original.defaultAccessId}
                    variant="outline-secondary"
                    className="btn-sm mr-1"
                    title="Exportera resultat som PDF">
                    {downloadingReportId === row.row.original.defaultAccessId
                      ? <Spinner animation="border" size="sm" className="mr-1" />
                      : <FontAwesomeIcon icon={faFilePdf} className="mr-1" />
                    }
                    PDF
                  </Button>
                }
                <Button
                  variant="outline-danger"
                  className="btn-sm"
                  onClick={() => setDeleteTarget(row.row.original)}>
                  Ta bort
                </Button>
              </div>
            )
          },
        ],
      }
    ],
    [quizName, downloadingReportId, downloadReport]
  );

  return (
    <div id="userMainPage">
      <div>
        {loading && (
          <Container>
            <div className="d-flex justify-content-center align-items-center h-100">
              <Spinner animation="border" />
            </div>
          </Container>
        )}

        {!loading &&
          <div>
            <AdminHeader />
            <div className="adminContentWrapper">

              <Button variant="outline-secondary" size="sm" onClick={() => window.history.back()}>Tillbaka</Button>

              {quizInstanceList.length > 0 &&
                <div id="userQuizList" className="mt-3">
                  <Table columns={columns} data={quizInstanceList} />
                </div>
              }

            </div>
          </div>
        }
      </div>

      <Modal show={!!deleteTarget} onHide={() => setDeleteTarget(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Ta bort omgång</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Vill du ta bort omgången startad{' '}
          <strong>
            {deleteTarget?.executedAt
              ? new Date(deleteTarget.executedAt).toLocaleString()
              : 'ej startad'}
          </strong>?
          Detta går inte att ångra.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteTarget(null)}>Avbryt</Button>
          <Button variant="danger" disabled={deleting} onClick={handleDeleteConfirm}>
            {deleting ? 'Tar bort...' : 'Ta bort'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
